import { type CSSProperties, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, Button, Card, DataTable, Dialog, Field, Input, Select, type Column } from '../../design';
import { api, ApiError } from '../../lib/api';
import { ROLE_LABELS, type AdminUser, type Role } from '../../lib/types';

const ROLES: Role[] = ['admin', 'practitioner', 'client'];
const roleOptions = ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }));

const banner: CSSProperties = { padding: 'var(--space-3) var(--space-5)', borderRadius: 'var(--radius-md)', font: 'var(--type-body-sm)' };

export function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Add-user modal
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('client');
  const [password, setPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function load() {
    try {
      const r = await api.get<{ users: AdminUser[] }>('/admin/users');
      setUsers(r.users);
    } catch {
      setError('Could not load users.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function resetForm() {
    setName('');
    setEmail('');
    setRole('client');
    setPassword('');
    setFormError(null);
  }

  function openModal() {
    resetForm();
    setNotice(null);
    setError(null);
    setOpen(true);
  }

  async function onCreate() {
    setFormError(null);
    if (!name.trim() || !email.trim() || password.length < 8) {
      setFormError('Name, email, and a password of at least 8 characters are required.');
      return;
    }
    setCreating(true);
    try {
      await api.post('/admin/users', { email, name, role, password });
      setOpen(false);
      resetForm();
      setNotice('User created.');
      await load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not create user.');
    } finally {
      setCreating(false);
    }
  }

  async function changeRole(id: string, next: Role) {
    setNotice(null);
    setError(null);
    try {
      await api.patch(`/admin/users/${id}`, { role: next });
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, role: next } : u)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update role.');
    }
  }

  async function resetPassword(u: AdminUser) {
    const next = window.prompt(`New password for ${u.name} (min 8 characters):`);
    if (next === null) return;
    if (next.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    try {
      await api.post(`/admin/users/${u.id}/password`, { password: next });
      setNotice(`Password reset for ${u.name}.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reset password.');
    }
  }

  async function removeUser(u: AdminUser) {
    if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      await api.del(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      setNotice(`Deleted ${u.name}.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete user.');
    }
  }

  const columns: Column<AdminUser>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (u) => (
        <span style={{ color: 'var(--text-strong)', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          {u.name}
          {u.id === me?.id && <Badge tone="brand">You</Badge>}
        </span>
      ),
    },
    { key: 'email', header: 'Email' },
    {
      key: 'role',
      header: 'Role',
      render: (u) => (
        <Select
          value={u.role}
          disabled={u.id === me?.id}
          options={roleOptions}
          onChange={(e) => changeRole(u.id, e.target.value as Role)}
          style={{ height: 'var(--control-h-sm)', width: 170 }}
        />
      ),
    },
    {
      key: 'isPractitioner',
      header: 'Practitioner',
      render: (u) => (u.isPractitioner ? <Badge tone="success">Practitioner</Badge> : <span style={{ color: 'var(--text-muted)' }}>—</span>),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (u) => (
        <span style={{ display: 'inline-flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="ghost" onClick={() => resetPassword(u)}>
            Reset password
          </Button>
          {u.id !== me?.id && (
            <Button size="sm" variant="ghost" onClick={() => removeUser(u)}>
              Delete
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      {notice && <div style={{ ...banner, background: 'var(--sage-100)', color: 'var(--sage-600)' }}>{notice}</div>}
      {error && <div style={{ ...banner, background: 'var(--clay-100)', color: 'var(--clay-600)' }}>{error}</div>}

      <Card
        pad="none"
        title={loading ? 'Users' : `Users (${users.length})`}
        action={
          <Button icon="user-plus" size="sm" onClick={openModal}>
            Add user
          </Button>
        }
      >
        {loading ? <div style={{ padding: 'var(--space-6)', color: 'var(--text-muted)' }}>Loading…</div> : <DataTable rows={users} columns={columns} />}
      </Card>

      <Dialog
        open={open}
        title="Add a user"
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={onCreate} disabled={creating}>
              {creating ? 'Adding…' : 'Add user'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <Field label="Full name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" />
          </Field>
          <Field label="Email" hint="They'll sign in with this address.">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com" />
          </Field>
          <Field label="Role">
            <Select value={role} onChange={(e) => setRole(e.target.value as Role)} options={roleOptions} />
          </Field>
          <Field label="Temporary password" hint="At least 8 characters. They can change it after signing in.">
            <Input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="min 8 characters" />
          </Field>
          {formError && <span style={{ color: 'var(--status-danger)', font: 'var(--type-body-sm)' }}>{formError}</span>}
        </div>
      </Dialog>
    </>
  );
}
